/**
 * The 8 mascot states — each is an FSM node.
 * Idle is the default. Happy is the money moment.
 */
export const STATES = [
  'idle',
  'hover',
  'listening',
  'thinking',
  'speaking',
  'happy',
  'confused',
  'sleeping',
] as const;

export type MascotState = (typeof STATES)[number];

/**
 * Transition matrix — milliseconds for each allowed move.
 * `null` means no direct transition; the FSM must route through idle.
 */
export const TRANSITIONS: Readonly<
  Record<MascotState, Readonly<Record<MascotState, number | null>>>
> = {
  idle: {
    idle: 0,
    hover: 180,
    listening: 240,
    thinking: 280,
    speaking: 280,
    happy: 400,
    confused: 280,
    sleeping: 2000,
  },
  hover: {
    idle: 240,
    hover: 0,
    listening: 240,
    thinking: 280,
    speaking: 280,
    happy: 400,
    confused: 280,
    sleeping: null,
  },
  listening: {
    idle: 240,
    hover: null,
    listening: 0,
    thinking: 240,
    speaking: 240,
    happy: 400,
    confused: 280,
    sleeping: null,
  },
  thinking: {
    idle: 240,
    hover: null,
    listening: null,
    thinking: 0,
    speaking: 180,
    happy: 400,
    confused: 280,
    sleeping: null,
  },
  speaking: {
    idle: 240,
    hover: null,
    listening: 240,
    thinking: 240,
    speaking: 0,
    happy: 400,
    confused: 280,
    sleeping: null,
  },
  happy: {
    idle: 400,
    hover: null,
    listening: null,
    thinking: null,
    speaking: null,
    happy: 0,
    confused: null,
    sleeping: null,
  },
  confused: {
    idle: 240,
    hover: 240,
    listening: null,
    thinking: 280,
    speaking: null,
    happy: null,
    confused: 0,
    sleeping: null,
  },
  sleeping: {
    idle: 800,
    hover: null,
    listening: null,
    thinking: null,
    speaking: null,
    happy: null,
    confused: null,
    sleeping: 0,
  },
};

export function canTransition(from: MascotState, to: MascotState): boolean {
  return TRANSITIONS[from][to] !== null;
}

export function durationMs(from: MascotState, to: MascotState): number {
  const d = TRANSITIONS[from][to];
  if (d === null) {
    throw new Error(`Illegal mascot transition: ${from} → ${to}`);
  }
  return d;
}
